import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, FileDown, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import ModelPicker from "@/components/ModelPicker";

function Quadrant({ label, color, items, onChange }) {
  const [text, setText] = useState("");
  return (
    <Card className="border-slate-200 shadow-none">
      <div className="px-4 py-2 text-xs uppercase tracking-widest font-mono-plex text-white" style={{ backgroundColor: color }}>{label}</div>
      <div className="p-4 space-y-2">
        {items.length === 0 && <div className="text-xs text-slate-400">No items yet.</div>}
        {items.map((it, i) => (
          <div key={i} className="flex items-start justify-between gap-2 text-sm">
            <span className="text-slate-800">• {it}</span>
            <button className="text-slate-300 hover:text-rose-500 text-xs" onClick={() => onChange(items.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <Input value={text} onChange={e => setText(e.target.value)} placeholder="Add item…" className="text-sm" />
          <Button size="sm" variant="outline" onClick={() => { if (text.trim()) { onChange([...items, text.trim()]); setText(""); } }}>Add</Button>
        </div>
      </div>
    </Card>
  );
}

export default function SWOTs() {
  const [rows, setRows] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [gen, setGen] = useState(false);
  const [genFac, setGenFac] = useState("");
  const [genNotes, setGenNotes] = useState("");
  const [genModel, setGenModel] = useState("claude-sonnet-5");
  const [genBusy, setGenBusy] = useState(false);
  const [form, setForm] = useState({ facility_id: "", strengths: [], weaknesses: [], opportunities: [], threats: [], context_notes: "" });
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/swots").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/facilities").then(({ data }) => setFacilities(data)); }, []);

  const submit = async () => {
    try { await api.post("/swots", form); toast.success("SWOT saved"); setOpen(false); setForm({ facility_id: "", strengths: [], weaknesses: [], opportunities: [], threats: [], context_notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const runAI = async () => {
    if (!genFac) { toast.error("Pick a facility"); return; }
    setGenBusy(true);
    try { await api.post("/swots/generate", { facility_id: genFac, context_notes: genNotes, model: genModel }); toast.success("AI SWOT generated"); setGen(false); setGenFac(""); setGenNotes(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setGenBusy(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete SWOT?")) return; await api.delete(`/swots/${id}`); load(); };

  const downloadPdf = async (id) => {
    const token = localStorage.getItem("aceh_token") || "";
    const resp = await fetch(`${API}/swots/${id}/pdf`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `swot-${id}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="swots-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Strategic</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black">SWOT</h1>
          <p className="text-slate-500 mt-1 text-sm">Manual or AI-assisted analysis per facility.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={gen} onOpenChange={setGen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="swot-ai-btn"><Sparkles className="w-4 h-4 mr-1" /> AI generate</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Generate SWOT with AI</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Facility</Label>
                  <Select value={genFac} onValueChange={setGenFac}>
                    <SelectTrigger data-testid="swot-ai-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
                    <SelectContent>{facilities.map(f => <SelectItem key={f.facility_id} value={f.facility_id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Model</Label><ModelPicker value={genModel} onChange={setGenModel} testId="swot-ai-model" /></div>
                <div><Label>Extra context (optional)</Label><Textarea rows={4} value={genNotes} onChange={e => setGenNotes(e.target.value)} placeholder="Recent conversations, competitor moves, etc." /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setGen(false)}>Cancel</Button><Button onClick={runAI} disabled={genBusy} data-testid="swot-ai-run" className="bg-slate-900 hover:bg-slate-800">{genBusy ? "Generating…" : "Generate"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="swot-new-btn"><Plus className="w-4 h-4 mr-1" /> New</Button></DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New SWOT</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Facility</Label>
                  <Select value={form.facility_id} onValueChange={v => setForm({ ...form, facility_id: v })}>
                    <SelectTrigger data-testid="swot-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
                    <SelectContent>{facilities.map(f => <SelectItem key={f.facility_id} value={f.facility_id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Quadrant label="Strengths" color="#059669" items={form.strengths} onChange={v => setForm({ ...form, strengths: v })} />
                  <Quadrant label="Weaknesses" color="#E11D48" items={form.weaknesses} onChange={v => setForm({ ...form, weaknesses: v })} />
                  <Quadrant label="Opportunities" color="#0F172A" items={form.opportunities} onChange={v => setForm({ ...form, opportunities: v })} />
                  <Quadrant label="Threats" color="#F97316" items={form.threats} onChange={v => setForm({ ...form, threats: v })} />
                </div>
                <div><Label>Context notes</Label><Textarea rows={3} value={form.context_notes} onChange={e => setForm({ ...form, context_notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="swot-save" className="bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-3">
        {rows.length === 0 && <Card className="p-8 text-center text-slate-400 border-slate-200 shadow-none">No SWOTs yet.</Card>}
        {rows.map(s => (
          <Card key={s.swot_id} className="p-4 border-slate-200 shadow-none row-enter cursor-pointer" onClick={() => setSelected(s)} data-testid={`swot-row-${s.swot_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium flex items-center gap-2">{s.facility_name} {s.ai_generated && <Badge variant="outline" className="text-[10px]">AI</Badge>}</div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono-plex">{(s.updated_at || "").slice(0, 10)} · S{s.strengths.length}/W{s.weaknesses.length}/O{s.opportunities.length}/T{s.threats.length}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadPdf(s.swot_id); }} data-testid={`swot-pdf-${s.swot_id}`}><FileDown className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); del(s.swot_id); }}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* View Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {selected && (<>
            <DialogHeader><DialogTitle>SWOT — {selected.facility_name}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[["Strengths", "#059669", selected.strengths], ["Weaknesses", "#E11D48", selected.weaknesses], ["Opportunities", "#0F172A", selected.opportunities], ["Threats", "#F97316", selected.threats]].map(([lbl, col, items]) => (
                <Card key={lbl} className="border-slate-200 shadow-none">
                  <div className="px-4 py-2 text-xs uppercase tracking-widest font-mono-plex text-white" style={{ backgroundColor: col }}>{lbl}</div>
                  <div className="p-4 space-y-1.5 text-sm">
                    {items.length === 0 ? <div className="text-xs text-slate-400">—</div> : items.map((it, i) => <div key={i}>• {it}</div>)}
                  </div>
                </Card>
              ))}
            </div>
            {selected.context_notes && <div className="mt-4 text-sm text-slate-700 whitespace-pre-wrap"><div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500 mb-1">Context</div>{selected.context_notes}</div>}
            <DialogFooter><Button variant="outline" onClick={() => downloadPdf(selected.swot_id)}><FileDown className="w-4 h-4 mr-1" /> Download PDF</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
